import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useCjmsStore } from './CJMSView/store/useCjmsStore';
import { JourneyDashboard } from './CJMSView/components/JourneyDashboard';
import { ClientDetailView } from './CJMSView/components/ClientDetailView';
import { ActionPanel } from './CJMSView/components/ActionPanel';
import { AddLeadForm } from './CJMSView/components/AddLeadForm';
import { cjmsApi, CJMS_STAGES } from './CJMSView/api/cjmsApi';

type StageFilter = string | 'all';

export const CJMSView = () => {
    const { clients, selectedClient, analytics, isLoading, error, actions } = useCjmsStore();
    const [stageFilter, setStageFilter] = useState<StageFilter>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddLeadForm, setShowAddLeadForm] = useState(false);

    useEffect(() => {
        actions.loadData();
    }, [actions]);

    const visibleClients = useMemo(() => {
        return clients.filter(c => {
            if (stageFilter !== 'all' && c.currentStage !== stageFilter) {return false;}
            if (searchTerm.trim()) {
                const q = searchTerm.toLowerCase();
                return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
            }
            return true;
        });
    }, [clients, stageFilter, searchTerm]);

    const handleStageClick = (stage: string) => {
        if (stage === stageFilter) {
            setStageFilter('all');
            return;
        }
        setStageFilter(stage);
        const firstMatch = clients.find(c => c.currentStage === stage);
        if (firstMatch) {actions.selectClient(firstMatch);}
    };

    // Stable callbacks — prevent ActionPanel from re-fetching on every parent render
    const handleFetchScript = useCallback(
        async (email: string) => cjmsApi.fetchSuggestedScript(email),
        []
    );

    // Handle stage change with lead → customer migration
    const handleStageChange = useCallback(
        async (nextStage: string) => {
            if (!selectedClient) {return;}
            // Lead → Customer migration: if a lead moves to "Lead Conversion", create a Customers entry
            if (selectedClient.source === 'lead' && nextStage === 'Lead Conversion') {
                await cjmsApi.updateClientStage(selectedClient.id, nextStage);
                // Refresh data to show the new customer
                await actions.loadData();
            } else {
                await actions.updateStage(selectedClient.id, nextStage);
            }
        },
        [selectedClient, actions]
    );

    if (error) {
        return (
            <div className="flex items-center justify-center h-full p-6">
                <div className="p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
                    Error loading CJMS data: {error}
                    <button onClick={() => actions.loadData()} className="ml-4 underline font-bold">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full p-6 bg-slate-50 space-y-6 overflow-y-auto">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Customer Journey Management</h1>
                    <p className="text-sm text-slate-500">
                        {clients.length} total · {clients.filter(c => c.source === 'lead').length} leads · {clients.filter(c => c.source === 'customer').length} customers
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="search"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="🔍 Search name or email…"
                        className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-200 w-64"
                    />
                    <button
                        onClick={() => setShowAddLeadForm(true)}
                        className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-full hover:bg-amber-700 transition-colors"
                    >
                        + Add Lead
                    </button>
                    {stageFilter !== 'all' && (
                        <button
                            onClick={() => setStageFilter('all')}
                            className="px-3 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full hover:bg-indigo-200"
                        >
                            Stage: {stageFilter} ✕
                        </button>
                    )}
                    <button
                        onClick={() => actions.loadData()}
                        className="px-3 py-1.5 bg-white border border-slate-200 text-xs font-medium text-slate-600 rounded-full hover:bg-slate-50"
                    >
                        ↻ Refresh
                    </button>
                </div>
            </div>

            {analytics && (
                <JourneyDashboard
                    analytics={analytics}
                    totalClients={visibleClients.length}
                    activeFilter={stageFilter}
                    onStageClick={handleStageClick}
                />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Client Directory */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="p-4 bg-white rounded-xl shadow-sm border border-slate-200 max-h-[600px] overflow-y-auto">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-semibold text-slate-800">
                                Client Directory
                                <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold">
                                    {visibleClients.length}
                                </span>
                            </h3>
                        </div>
                        {visibleClients.length === 0 ? (
                            <div className="p-6 text-center border-2 border-dashed border-slate-200 rounded-lg">
                                <p className="text-sm text-slate-400">
                                    {clients.length === 0
                                        ? 'No clients yet. Sync your CRM to populate the journey.'
                                        : 'No clients match the current filter.'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {visibleClients.map(client => (
                                    <motion.button
                                        type="button"
                                        key={client.id}
                                        whileHover={{ x: 2 }}
                                        onClick={() => actions.selectClient(client)}
                                        className={`w-full text-left p-3 rounded-lg transition-all ${
                                            selectedClient?.id === client.id
                                            ? 'bg-indigo-600 text-white shadow-md'
                                            : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                                        }`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div className="text-sm font-bold truncate flex-1">{client.name}</div>
                                            <span className={`shrink-0 ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                                selectedClient?.id === client.id
                                                ? 'bg-white/20 text-white'
                                                : client.source === 'lead'
                                                ? 'bg-amber-100 text-amber-700'
                                                : 'bg-indigo-100 text-indigo-700'
                                            }`}>
                                                {client.source}
                                            </span>
                                        </div>
                                        <div className={`text-[10px] truncate mt-0.5 ${
                                            selectedClient?.id === client.id ? 'text-indigo-100' : 'text-slate-500'
                                        }`}>{client.currentStage}</div>
                                    </motion.button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Detail & Actions */}
                <div className="lg:col-span-2 space-y-3">
                    {selectedClient ? (
                        <>
                            <ClientDetailView
                                client={selectedClient}
                                onStageChange={handleStageChange}
                                onBack={() => actions.selectClient(null)}
                                onUpdate={() => actions.loadData()}
                                onCommunicationLoaded={actions.updateClientCommunication}
                            />
                            <ActionPanel
                                client={selectedClient}
                                onFetchScript={handleFetchScript}
                            />
                        </>
                    ) : (
                        <>
                            {showAddLeadForm ? (
                                <AddLeadForm
                                    onSuccess={() => {
                                        setShowAddLeadForm(false);
                                        actions.loadData();
                                    }}
                                    onCancel={() => setShowAddLeadForm(false)}
                                />
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex items-center justify-center h-[400px] bg-white rounded-xl border-2 border-dashed border-slate-200 text-slate-400"
                                >
                                    <div className="text-center">
                                        <div className="text-4xl mb-2 opacity-30">◉</div>
                                        <p>Select a client to view journey details and intelligence</p>
                                    </div>
                                </motion.div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {isLoading && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="fixed bottom-6 right-6 p-3 bg-indigo-600 text-white rounded-full shadow-lg flex items-center gap-2"
                >
                    <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    <span className="text-xs font-medium">Syncing…</span>
                </motion.div>
            )}
        </div>
    );
};

export default CJMSView;